import React, { useState, useCallback } from "react"
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Button } from "@mui/material"
import { useColorScheme } from "@mui/material/styles"
import DarkMode from "@mui/icons-material/DarkMode"
import LightMode from "@mui/icons-material/LightMode"
import SettingsBrightness from "@mui/icons-material/SettingsBrightness"
import Tooltip from "components/material/tooltip"
import { useThemePreference } from "hooks/use-theme-preference"
import { useAreThemesEnabled } from "resources/account"
import { useIsSuperuser } from "hooks/users"
import {
  applyMuiSchemeFromPreference,
  THEME_PREFERENCE_KEY,
  VALID_THEME_PREFERENCES,
} from "../../../styles/theme-mode-config"
import type { ThemeMode } from "./theme-preference"

import "./style.scss"

const MODES: { value: ThemeMode, label: string, Icon: typeof LightMode }[] = [
  { value: "light-colors", label: "Light", Icon: LightMode },
  { value: "dark-colors", label: "Dark", Icon: DarkMode },
  { value: "system", label: "System", Icon: SettingsBrightness },
]

function isThemeMode(value: unknown): value is ThemeMode {
  return typeof value === "string" && VALID_THEME_PREFERENCES.includes(value)
}

function isThemeModeSaver(value: unknown): value is (mode: ThemeMode) => void {
  return typeof value === "function"
}

function getStoredPreferenceFromLocalStorage(): ThemeMode | null {
  try {
    const raw = localStorage.getItem(THEME_PREFERENCE_KEY)
    return raw !== null && isThemeMode(raw) ? raw : null
  } catch {
    return null
  }
}

export interface ThemeModeToggleProps {
  inline?: boolean
  iconButton?: boolean
}

export default function ThemeModeToggle({ inline, iconButton = true }: ThemeModeToggleProps) {
  const { setMode, setColorScheme } = useColorScheme()
  const { storedMode, saveThemeMode: saveThemeModeRaw } = useThemePreference()
  const [preferenceOverride, setPreferenceOverride] = useState<ThemeMode | null>(null)

  const isSuperuser = useIsSuperuser()
  const areThemesEnabled = useAreThemesEnabled()
  const canUseThemes = areThemesEnabled || isSuperuser
  const saveThemeMode = useCallback(
    (mode: ThemeMode) => {
      if (isThemeModeSaver(saveThemeModeRaw)) {
        saveThemeModeRaw(mode)
      }
    },
    [saveThemeModeRaw],
  )
  const effectivePreference = preferenceOverride ?? storedMode ?? getStoredPreferenceFromLocalStorage() ?? "system"
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const applyScheme = useCallback(
    (value: ThemeMode) => {
      applyMuiSchemeFromPreference(value, { setMode, setColorScheme })
      saveThemeMode(value)
      try {
        localStorage.setItem(THEME_PREFERENCE_KEY, value)
      } catch {
        // Do nothing
      }
      setPreferenceOverride(value)
      handleClose()
    },
    [setMode, setColorScheme, saveThemeMode, handleClose],
  )

  const currentConfig =
    MODES.find((m) => m.value === effectivePreference) ?? MODES[2]
  const CurrentIcon = currentConfig.Icon

  const button =
    inline && !iconButton ? (
      <Button
        id="theme-mode-button"
        onClick={handleOpen}
        aria-label="Theme mode"
        aria-controls={open ? "theme-mode-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        className="theme-mode-toggle"
      >
        <CurrentIcon />
      </Button>
    ) : (
      <IconButton
        id="theme-mode-button"
        onClick={handleOpen}
        size={inline ? "large" : "small"}
        aria-label="Theme mode"
        aria-controls={open ? "theme-mode-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        className={inline ? "theme-mode-toggle round" : undefined}
        sx={inline ? undefined : { position: "fixed", top: 12, right: 12, zIndex: 1300 }}
      >
        <CurrentIcon />
      </IconButton>
    )

  if (!canUseThemes) {
    return null
  }
  return (
    <>
      {inline ? (
        <span className="components_theme-mode-toggle">
          <Tooltip title="Theme mode">
            {button}
          </Tooltip>
        </span>
      ) : (
        button
      )}
      <Menu
        id="theme-mode-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ list: { "aria-labelledby": "theme-mode-button" } }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {MODES.map(({ value, label, Icon }) => (
          <MenuItem
            key={value}
            onClick={() => applyScheme(value)}
            selected={value === effectivePreference}
          >
            <ListItemIcon>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
